import { useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import Prism from 'prismjs';

import type { Query, InstructionParam } from '../../data/instructions.generated';
import { useQueryRunner } from '../../hooks/useQueryRunner';
import { useCodeFlavor, type CodeFlavor } from '../../lib/code-flavor-context';
import { highlightedJson } from '../../lib/json-tree';
import { MinimalCodeBlock, type CodeTab } from './CodeBlock';

interface QueryPageProps {
  query: Query;
}

const HIGHLIGHTABLE_KINDS: ReadonlySet<string> = new Set([
  'amount',
  'duration',
  'schedule',
  'number',
  'boolean',
  'unknown',
]);

function highlightCode(source: string): string {
  return Prism.highlight(source || ' ', Prism.languages.typescript, 'typescript');
}

function cleanExample(raw: string | undefined): string {
  if (!raw) return '';
  const fence = raw.match(/```(?:[a-zA-Z]+)?\s*([\s\S]*?)```/);
  return (fence ? fence[1] : raw).trim();
}

function quoteLiteral(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return "''";
  if (trimmed === 'true' || trimmed === 'false' || trimmed === 'null') return trimmed;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;
  if (/^[{[]/.test(trimmed)) return trimmed;
  return `'${trimmed.replace(/'/g, "\\'")}'`;
}

function buildSnippet(query: Query, values: Record<string, string>, flavor: CodeFlavor): string {
  const importPath = flavor === 'kit' ? '@sly-rentals/core' : '@sly-rentals/core/legacy';
  if (query.params.length === 0) {
    return [
      `import { ${query.name} } from '${importPath}';`,
      '',
      `const result = await ${query.name}();`,
    ].join('\n');
  }
  const lines: string[] = [
    `import { ${query.name} } from '${importPath}';`,
    '',
    `const result = await ${query.name}({`,
  ];
  for (const p of query.params) {
    const v = values[p.name] ?? '';
    if (p.optional && !v.trim()) continue;
    lines.push(`  ${p.name}: ${quoteLiteral(v)},`);
  }
  lines.push('});');
  return lines.join('\n');
}

interface SchemaRowProps {
  param: InstructionParam;
  value: string;
  onChange: (next: string) => void;
}

function SchemaRow({ param, value, onChange }: SchemaRowProps) {
  const example = cleanExample(param.examples[0]);
  const defaultValue = cleanExample(param.defaultValue);
  const placeholder = example || (defaultValue ? `default: ${defaultValue}` : 'value');
  const highlight = HIGHLIGHTABLE_KINDS.has(param.typeKind) && value.length > 0;
  const html = useMemo(() => (highlight ? highlightCode(value) : ''), [highlight, value]);
  return (
    <div className="schema-row">
      <div className="schema-meta">
        <span className="schema-name">{param.name}</span>
        <span className="schema-type">{param.typeKind}</span>
        <span className={`schema-tag ${param.optional ? 'optional' : 'required'}`}>
          {param.optional ? 'optional' : 'required'}
        </span>
      </div>
      {param.description && <p className="schema-desc">{param.description}</p>}
      <div className={`schema-input-wrap${highlight ? ' is-highlighted' : ''}`}>
        {highlight && (
          <pre className="schema-input-overlay" aria-hidden>
            <code dangerouslySetInnerHTML={{ __html: html }} />
          </pre>
        )}
        <input
          className="schema-input"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          spellCheck={false}
        />
      </div>
    </div>
  );
}

function isAdvancedParam(p: InstructionParam): boolean {
  return p.optional;
}

export function QueryPage({ query }: QueryPageProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { flavor, setFlavor } = useCodeFlavor();
  const runner = useQueryRunner();

  const regularParams = useMemo(
    () => query.params.filter(p => !isAdvancedParam(p)),
    [query.params]
  );
  const advancedParams = useMemo(
    () => query.params.filter(p => isAdvancedParam(p)),
    [query.params]
  );

  useEffect(() => {
    setValues({});
    setShowAdvanced(false);
    runner.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.name]);

  const update = (name: string, next: string) => setValues(prev => ({ ...prev, [name]: next }));

  const tabs = useMemo<CodeTab[]>(
    () => [
      { id: 'kit', label: '@solana/kit', source: buildSnippet(query, values, 'kit') },
      { id: 'web3js', label: '@solana/web3.js', source: buildSnippet(query, values, 'web3js') },
    ],
    [query, values]
  );

  return (
    <div className="ix-grid">
      <div>
        <div className="ix-head">
          <div className="ix-kicker">
            <span className="br">[</span>
            <span className="group">&nbsp;query</span>
            <span className="br">/</span>
            <span className="name">&nbsp;{query.name}&nbsp;</span>
            <span className="br">]</span>
          </div>
          <h1 className="ix-title">
            {query.name}
            <a
              href={`https://wuwei-labs.github.io/srsly/api/srsly/functions/index.${query.name}.html`}
              target="_blank"
              rel="noopener"
              className="ix-docs-link"
              title={`Open ${query.name} reference docs`}
            >
              docs <ExternalLink size={11} strokeWidth={1.6} />
            </a>
            <span className="ix-badges">
              <span className="ix-badge">→ {query.returnType}</span>
            </span>
          </h1>
          {query.description && <p className="ix-sub">{query.description}</p>}
        </div>

        <h2 className="ix-section-label">Parameters</h2>
        {query.params.length === 0 ? (
          <p className="dim mono" style={{ padding: '14px 0', fontSize: 12 }}>
            No parameters.
          </p>
        ) : (
          <>
            {regularParams.length > 0 && (
              <div className="schema">
                {regularParams.map(p => (
                  <SchemaRow
                    key={p.name}
                    param={p}
                    value={values[p.name] ?? ''}
                    onChange={v => update(p.name, v)}
                  />
                ))}
              </div>
            )}
            {advancedParams.length > 0 && (
              <div className="schema-advanced">
                <button
                  type="button"
                  className="schema-advanced-toggle"
                  onClick={() => setShowAdvanced(v => !v)}
                  aria-expanded={showAdvanced}
                >
                  <span className="caret">{showAdvanced ? '▾' : '▸'}</span>
                  Advanced parameters
                  <span className="count">({advancedParams.length})</span>
                </button>
                {showAdvanced && (
                  <div className="schema">
                    {advancedParams.map(p => (
                      <SchemaRow
                        key={p.name}
                        param={p}
                        value={values[p.name] ?? ''}
                        onChange={v => update(p.name, v)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <aside
        style={{
          position: 'sticky',
          top: 24,
          alignSelf: 'start',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <h2 className="ix-section-label">Live preview</h2>
        <MinimalCodeBlock
          tabs={tabs}
          active={flavor}
          onActiveChange={id => setFlavor(id as CodeFlavor)}
        />
        <div className="console" data-state={runner.status.kind}>
          {runner.status.kind !== 'idle' && (
            <div className="console-head">
              <span className="dot" />
              <span>
                {runner.status.kind === 'running'
                  ? 'running…'
                  : runner.status.kind === 'ok'
                    ? 'ok'
                    : 'failed'}
              </span>
              {(runner.status.kind === 'ok' || runner.status.kind === 'error') && (
                <button type="button" className="reset" onClick={runner.reset}>
                  reset
                </button>
              )}
            </div>
          )}
          <div className="console-body">
            {runner.status.kind === 'idle' && (
              <div className="console-cta">
                <div>
                  <span className="lbl-line">run query</span>
                  <div className="hint">Calls {query.name}() and prints the returned value.</div>
                </div>
                <button
                  type="button"
                  className="sly-btn primary"
                  onClick={() => runner.run(query, values)}
                >
                  Run query
                </button>
              </div>
            )}
            {runner.status.kind === 'running' && (
              <div className="hint" style={{ color: 'var(--ink-dim)' }}>
                Fetching · computing…
              </div>
            )}
            {runner.status.kind === 'ok' && (
              <div className="console-fetch">{highlightedJson(runner.status.data)}</div>
            )}
            {runner.status.kind === 'error' && (
              <div className="console-err">{runner.status.message}</div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
